import { Container, Header, SpaceBetween, Box, StatusIndicator, Table } from '@cloudscape-design/components';
import ExpandableSection from "@cloudscape-design/components/expandable-section";

/**
 * Parsea un string con formato "key: value\nkey2: value2" a array de objetos
 * @param {string} text - Texto a parsear
 * @param {Function} getLabel - Funcion para obtener labels traducidos desde la DB
 * @param {string} category - Categoria para traducciones
 * @param {string} parentKey - Clave padre para construir la clave completa
 * @returns {Array<{key: string, label: string, value: string}>}
 */
const parseKeyValueString = (text, getLabel, category, parentKey) => {
  if (!text || typeof text !== 'string') return [];

  const lines = text.split('\n').filter(line => line.trim());

  return lines.map(line => {
    const colonIndex = line.indexOf(':');
    if (colonIndex > 0) {
      const key = line.substring(0, colonIndex).trim();
      const value = line.substring(colonIndex + 1).trim();
      // Construir clave completa para buscar traduccion en DB
      const fullKey = parentKey ? `${parentKey}.${key}` : key;
      // Usar getLabel de la DB si esta disponible, sino usar la clave original
      const label = getLabel
        ? getLabel(fullKey, category, key)
        : key;
      return { key, label, value };
    }
    return { key: '', label: '', value: line.trim() };
  }).filter(item => item.value);
};

const STATUS_DEFINITIONS = [
  { keys: ['cumple'], type: 'success', label: 'Cumple' },
  { keys: ['nocumple'], type: 'error', label: 'No cumple' },
  { keys: ['si'], type: 'success', label: 'Si' },
  { keys: ['no'], type: 'error', label: 'No' },
  { keys: ['correcto'], type: 'success', label: 'Correcto' },
  { keys: ['parcialmenteincorrecto'], type: 'warning', label: 'Parcialmente incorrecto' },
  { keys: ['incorrecto'], type: 'error', label: 'Incorrecto' },
  { keys: ['na'], type: 'info', label: 'N/A' },
  { keys: ['noaplica'], type: 'info', label: 'N/A' },
  { keys: ['sindatos'], type: 'info', label: 'S/D' },
  { keys: ['observable'], type: 'warning', label: 'Observable' },
  { keys: ['rechazable'], type: 'error', label: 'Rechazable' },
  { keys: ['positivo'], type: 'success', label: 'Positivo' },
  { keys: ['neutro'], type: 'warning', label: 'Neutro' },
  { keys: ['negativo'], type: 'error', label: 'Negativo' },
  { keys: ['om'], type: 'warning', label: 'OM' },
  { keys: ['critico'], type: 'error', label: 'Critico' }
];

const STATUS_MAP = STATUS_DEFINITIONS.reduce((map, definition) => {
  definition.keys.forEach((key) => {
    map[key] = { type: definition.type, label: definition.label };
  });
  return map;
}, {});

const normalizeStatusValue = (value) => {
  if (typeof value !== 'string') return '';

  return value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ');
};

export const getStatusConfig = (value) => {
  const normalized = normalizeStatusValue(value);
  if (!normalized) return null;

  const compact = normalized.replace(/[^a-z]/g, '');
  return STATUS_MAP[compact] ?? null;
};

const isExpandableValue = (value) =>
  typeof value === 'string' && value.includes('\n') && value.includes(':');

const isSimpleEntryValue = (value) => {
  if (value === null || value === undefined) return true;
  if (Array.isArray(value)) return false;
  if (typeof value === 'object') return false;
  if (typeof value === 'string') return !isExpandableValue(value);
  return true;
};

const shouldRenderQuote = (value) => {
  if (typeof value !== 'string') return false;

  const trimmed = value.trim();
  if (!trimmed) return false;

  const words = trimmed.split(/\s+/).filter(Boolean);
  return trimmed.includes('\n') || words.length > 1 || trimmed.length > 24;
};

const formatInsightText = (value, { formatValue, category, baseKey }) => {
  const rawValue = typeof value === 'string' ? value : String(value ?? '');

  return formatValue
    ? formatValue(rawValue, { category, baseKey })
    : rawValue;
};

const renderTextBlock = (content) => (
  <span style={{ whiteSpace: 'pre-line' }}>{content}</span>
);

const renderStatusIndicator = (statusConfig) => (
  <StatusIndicator type={statusConfig.type}>
    {statusConfig.label}
  </StatusIndicator>
);

const renderQuote = (content) => (
  <Box className="insight-quote">
    {renderTextBlock(content)}
  </Box>
);

const renderStandaloneValue = (value, { formatValue, category, baseKey }) => {
  const rawValue = typeof value === 'string' ? value : String(value ?? '');
  const statusConfig = getStatusConfig(rawValue);
  const formattedValue = formatInsightText(rawValue, { formatValue, category, baseKey });

  if (statusConfig) {
    return renderStatusIndicator(statusConfig);
  }

  if (shouldRenderQuote(rawValue)) {
    return renderQuote(formattedValue);
  }

  return renderTextBlock(formattedValue);
};

/**
 * Renderiza los items hijos de una seccion expandida
 */
const ExpandedContent = ({ items, formatValue, category, parentKey }) => {
  if (!items || items.length === 0) {
    return <Box color="text-status-inactive">Sin datos</Box>;
  }

  const tableItems = [];
  const quoteItems = [];

  items.forEach((item, index) => {
    const rawValue = typeof item.value === 'string' ? item.value : String(item.value ?? '');
    const statusConfig = getStatusConfig(rawValue);
    const formattedValue = formatInsightText(rawValue, {
      formatValue,
      category,
      baseKey: item.baseKey ?? parentKey
    });
    const id = item.key || item.label || String(index);

    if (!statusConfig && shouldRenderQuote(rawValue)) {
      quoteItems.push({
        id,
        label: item.label,
        content: formattedValue
      });
      return;
    }

    tableItems.push({
      ...item,
      id,
      renderedValue: statusConfig
        ? renderStatusIndicator(statusConfig)
        : renderTextBlock(formattedValue)
    });
  });

  const showQuoteLabel = tableItems.length > 0 || quoteItems.length > 1;

  return (
    <SpaceBetween size="s">
      {tableItems.length > 0 && (
        <Table
          className="expandable-insight-table"
          variant="embedded"
          items={tableItems}
          columnDefinitions={[
            {
              id: 'label',
              header: 'Clave',
              cell: item => item.label,
              isRowHeader: true
            },
            {
              id: 'value',
              header: 'Valor',
              cell: item => item.renderedValue,
              wrapLines: true
            }
          ]}
          trackBy="id"
          ariaLabel={parentKey ? `Detalle de ${parentKey}` : 'Detalle de insights'}
        />
      )}
      {quoteItems.length > 0 && (
        <SpaceBetween size="xs">
          {quoteItems.map((item) => (
            <div key={item.id}>
              {showQuoteLabel && item.label && (
                <Box variant="awsui-key-label">{item.label}</Box>
              )}
              {renderQuote(item.content)}
            </div>
          ))}
        </SpaceBetween>
      )}
    </SpaceBetween>
  );
};

/**
 * Componente para mostrar secciones de insight con items expandibles
 *
 * @param {Object} props
 * @param {string} props.title - Titulo de la seccion (ej: "Evaluacion de calidad")
 * @param {Object} props.data - Objeto con los datos (ej: QualityEvaluation)
 * @param {string} props.category - Categoria para traducciones (ej: "QualityEvaluation")
 * @param {string} props.noDataText - Texto cuando no hay datos
 * @param {Function} props.formatValue - Funcion para formatear valores
 * @param {Function} props.getLabel - Funcion para obtener labels traducidos
 * @param {React.ReactNode} props.headerAction - Accion del header (ej: boton refresh)
 */
export const ExpandableInsightSection = ({
  title,
  data,
  category,
  noDataText = "No hay datos disponibles",
  formatValue,
  getLabel,
  headerAction
}) => {
  // Si no hay datos, mostrar mensaje
  if (!data || Object.keys(data).length === 0) {
    return (
      <Container
        className="insight-section-container"
        fitHeight={true}
        header={
          <Header variant="h2" actions={headerAction}>
            {title}
          </Header>
        }
      >
        <Box color="text-status-inactive">{noDataText}</Box>
      </Container>
    );
  }

  // Filtrar claves internas
  const entries = Object.entries(data).filter(([key]) => key !== 'insightsPrompt');
  const shouldRenderFlatTable =
    entries.length > 0 && entries.every(([, value]) => isSimpleEntryValue(value));

  if (shouldRenderFlatTable) {
    const flatItems = entries.map(([key, value]) => ({
      key,
      label: getLabel ? getLabel(key, category, key) : key,
      value,
      baseKey: key
    }));

    return (
      <Container
        className="insight-section-container"
        fitHeight={true}
        header={
          <Header variant="h2" actions={headerAction}>
            {title}
          </Header>
        }
      >
        <ExpandedContent
          items={flatItems}
          formatValue={formatValue}
          category={category}
          parentKey={null}
        />
      </Container>
    );
  }

  return (
    <Container
      className="insight-section-container"
      fitHeight={true}
      header={
        <Header variant="h2" actions={headerAction}>
          {title}
        </Header>
      }
    >
      <SpaceBetween size="m">
        {entries.map(([key, value]) => {
          // Obtener label traducido desde la DB (fallback a la clave original)
          const label = getLabel
            ? getLabel(key, category, key)
            : key;

          // Determinar si el valor tiene sub-items (contiene saltos de linea con :)
          const isExpandable = isExpandableValue(value);

          // Parsear sub-items si es expandible (pasando getLabel para obtener traducciones de la DB)
          const subItems = isExpandable ? parseKeyValueString(value, getLabel, category, key) : [];

          // Caso: objeto anidado
          if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            const nestedEntries = Object.entries(value).map(([k, v]) => {
              // Construir la clave completa para buscar traduccion en la DB
              const fullKey = `${key}.${k}`;
              return {
                key: k,
                label: getLabel ? getLabel(fullKey, category, k) : k,
                value: String(v)
              };
            });

            return (
              <ExpandableSection
                key={key}
                className="insight-expandable-section"
                headerText={label}
                variant="default"
                defaultExpanded={false}
              >
                <ExpandedContent
                  items={nestedEntries}
                  formatValue={formatValue}
                  category={category}
                  parentKey={key}
                />
              </ExpandableSection>
            );
          }

          // Caso: string con sub-items (expandible)
          if (isExpandable && subItems.length > 0) {
            return (
              <ExpandableSection
                key={key}
                className="insight-expandable-section"
                headerText={label}
                variant="default"
                defaultExpanded={false}
              >
                <ExpandedContent
                  items={subItems}
                  formatValue={formatValue}
                  category={category}
                  parentKey={key}
                />
              </ExpandableSection>
            );
          }

          // Caso: valor simple (no expandible)
          return (
            <ExpandableSection
              key={key}
              className="insight-expandable-section"
              headerText={label}
              variant="default"
              defaultExpanded={false}
            >
              <Box padding={{ left: "l" }}>
                {renderStandaloneValue(value, {
                  formatValue,
                  category,
                  baseKey: key
                })}
              </Box>
            </ExpandableSection>
          );
        })}
      </SpaceBetween>
    </Container>
  );
};

export default ExpandableInsightSection;
